/**
 * Mapping Canvas Component
 * Overlay canvas for drawing and displaying mapping regions
 */

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { MappingRegion, FieldName, FieldType, AVAILABLE_FIELDS } from './types';

// Font info from PDF
interface FontInfo {
  fontFamily: string;
  fontSize: number;
  fontColor: string;
}

interface MappingCanvasProps {
  width: number;
  height: number;
  scale: number;
  mappings: MappingRegion[];
  selectedMapping: string | null;
  drawingField: FieldName | null;
  onSelectMapping: (id: string | null) => void;
  onUpdateMapping: (id: string, updates: Partial<MappingRegion>) => void;
  onCreateMapping: (mapping: Omit<MappingRegion, 'id'>) => void;
  onDeleteMapping: (id: string) => void;
  pageNumber: number;
  getFontInfo?: (x: number, y: number, width: number, height: number, pageNum: number) => Promise<FontInfo | null>;
}

interface DrawState {
  isDrawing: boolean;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

interface DragState {
  isDragging: boolean;
  mappingId: string | null;
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
}

interface ResizeState {
  isResizing: boolean;
  mappingId: string | null;
  handle: 'se' | 'sw' | 'ne' | 'nw' | 'e' | 'w' | 'n' | 's' | null;
  startX: number;
  startY: number;
  originalMapping: MappingRegion | null;
}

const FIELD_COLORS: Record<FieldType, string> = {
  text: '#3B82F6',     // Blue
  date: '#10B981',     // Green
  currency: '#F59E0B', // Amber
  number: '#8B5CF6',   // Purple
  table: '#EF4444',    // Red
};

// Short labels for compact display inside boxes
const SHORT_LABELS: Record<string, string> = {
  'bankName': 'Bank',
  'bankAddress': 'BankAddr',
  'bankCity': 'BankCity',
  'bankState': 'BankSt',
  'bankZip': 'BankZip',
  'accountHolder': 'Name',
  'accountNumber': 'Acct#',
  'routingNumber': 'Routing',
  'statementPeriod': 'Period',
  'statementDate': 'StmtDate',
  'openingBalance': 'OpenBal',
  'closingBalance': 'CloseBal',
  'totalDeposits': 'TotDep',
  'totalWithdrawals': 'TotWdrl',
  'address1': 'Addr1',
  'address2': 'Addr2',
  'address3': 'Addr3',
  'transactionTable': 'TxnTable',
};

export const MappingCanvas: React.FC<MappingCanvasProps> = ({
  width,
  height,
  scale,
  mappings,
  selectedMapping,
  drawingField,
  onSelectMapping,
  onUpdateMapping,
  onCreateMapping,
  onDeleteMapping,
  pageNumber,
  getFontInfo,
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [drawState, setDrawState] = useState<DrawState>({
    isDrawing: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
  });
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    mappingId: null,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
  });
  const [resizeState, setResizeState] = useState<ResizeState>({
    isResizing: false,
    mappingId: null,
    handle: null,
    startX: 0,
    startY: 0,
    originalMapping: null,
  });
  const [hoveredMapping, setHoveredMapping] = useState<string | null>(null);
  const [snapLines, setSnapLines] = useState<{ x: number | null; y: number | null }>({ x: null, y: null });

  // Snap threshold in PDF coordinates (not screen pixels)
  const SNAP_THRESHOLD = 5;

  // Filter mappings for current page
  const visibleMappings = mappings.filter(m => m.pageNumber === pageNumber);

  // Get mouse position relative to canvas
  const getMousePosition = useCallback((e: React.MouseEvent | MouseEvent): { x: number; y: number } => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / scale,
      y: (e.clientY - rect.top) / scale,
    };
  }, [scale]);

  // Check if point is inside a mapping
  const findMappingAtPoint = useCallback((x: number, y: number): MappingRegion | null => {
    // Check in reverse order (top-most first)
    for (let i = visibleMappings.length - 1; i >= 0; i--) {
      const m = visibleMappings[i];
      if (x >= m.x && x <= m.x + m.width && y >= m.y && y <= m.y + m.height) {
        return m;
      }
    }
    return null;
  }, [visibleMappings]);

  // Check if point is on a resize handle
  const findResizeHandle = useCallback((x: number, y: number, mapping: MappingRegion): ResizeState['handle'] => {
    const handleSize = 8 / scale;
    const { x: mx, y: my, width: mw, height: mh } = mapping;

    // Corners
    if (Math.abs(x - (mx + mw)) < handleSize && Math.abs(y - (my + mh)) < handleSize) return 'se';
    if (Math.abs(x - mx) < handleSize && Math.abs(y - (my + mh)) < handleSize) return 'sw';
    if (Math.abs(x - (mx + mw)) < handleSize && Math.abs(y - my) < handleSize) return 'ne';
    if (Math.abs(x - mx) < handleSize && Math.abs(y - my) < handleSize) return 'nw';

    // Edges
    if (Math.abs(x - (mx + mw)) < handleSize && y > my && y < my + mh) return 'e';
    if (Math.abs(x - mx) < handleSize && y > my && y < my + mh) return 'w';
    if (Math.abs(y - my) < handleSize && x > mx && x < mx + mw) return 'n';
    if (Math.abs(y - (my + mh)) < handleSize && x > mx && x < mx + mw) return 's';

    return null;
  }, [scale]);

  // Find snap positions for alignment with other mappings
  const findSnapPositions = useCallback((
    currentId: string,
    x: number,
    y: number,
    width: number,
    height: number
  ): { snappedX: number; snappedY: number; snapLineX: number | null; snapLineY: number | null } => {
    let snappedX = x;
    let snappedY = y;
    let snapLineX: number | null = null;
    let snapLineY: number | null = null;

    const otherMappings = visibleMappings.filter(m => m.id !== currentId);

    for (const other of otherMappings) {
      // === VERTICAL ALIGNMENT (X axis) ===

      // Snap left edge to other's left edge
      if (Math.abs(x - other.x) < SNAP_THRESHOLD) {
        snappedX = other.x;
        snapLineX = other.x;
      }
      // Snap right edge to other's right edge
      if (Math.abs((x + width) - (other.x + other.width)) < SNAP_THRESHOLD) {
        snappedX = other.x + other.width - width;
        snapLineX = other.x + other.width;
      }
      // Snap right edge to other's left edge (side by side)
      if (Math.abs((x + width) - other.x) < SNAP_THRESHOLD) {
        snappedX = other.x - width;
        snapLineX = other.x;
      }
      // Snap left edge to other's right edge (side by side)
      if (Math.abs(x - (other.x + other.width)) < SNAP_THRESHOLD) {
        snappedX = other.x + other.width;
        snapLineX = other.x + other.width;
      }

      // === HORIZONTAL ALIGNMENT (Y axis) ===

      // Snap top edge to other's top edge (same line)
      if (Math.abs(y - other.y) < SNAP_THRESHOLD) {
        snappedY = other.y;
        snapLineY = other.y;
      }
      // Snap bottom edge to other's bottom edge
      if (Math.abs((y + height) - (other.y + other.height)) < SNAP_THRESHOLD) {
        snappedY = other.y + other.height - height;
        snapLineY = other.y + other.height;
      }
      // Snap top to other's bottom (stacking vertically - for address lines)
      if (Math.abs(y - (other.y + other.height)) < SNAP_THRESHOLD) {
        snappedY = other.y + other.height;
        snapLineY = other.y + other.height;
      }
      // Snap bottom to other's top (stacking vertically)
      if (Math.abs((y + height) - other.y) < SNAP_THRESHOLD) {
        snappedY = other.y - height;
        snapLineY = other.y;
      }
    }

    return { snappedX, snappedY, snapLineX, snapLineY };
  }, [visibleMappings, SNAP_THRESHOLD]);

  // Handle mouse down
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const { x, y } = getMousePosition(e);

    // If we're in drawing mode
    if (drawingField) {
      setDrawState({
        isDrawing: true,
        startX: x,
        startY: y,
        currentX: x,
        currentY: y,
      });
      return;
    }

    // Check for resize handle on selected mapping
    if (selectedMapping) {
      const mapping = visibleMappings.find(m => m.id === selectedMapping);
      if (mapping) {
        const handle = findResizeHandle(x, y, mapping);
        if (handle) {
          setResizeState({
            isResizing: true,
            mappingId: selectedMapping,
            handle,
            startX: x,
            startY: y,
            originalMapping: { ...mapping },
          });
          return;
        }
      }
    }

    // Check if clicking on a mapping
    const clickedMapping = findMappingAtPoint(x, y);
    if (clickedMapping) {
      onSelectMapping(clickedMapping.id);

      // Start drag
      setDragState({
        isDragging: true,
        mappingId: clickedMapping.id,
        startX: x,
        startY: y,
        offsetX: x - clickedMapping.x,
        offsetY: y - clickedMapping.y,
      });
    } else {
      onSelectMapping(null);
    }
  }, [drawingField, getMousePosition, selectedMapping, visibleMappings, findResizeHandle, findMappingAtPoint, onSelectMapping]);

  // Handle mouse move
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const { x, y } = getMousePosition(e);

    // Drawing mode
    if (drawState.isDrawing) {
      setDrawState(prev => ({
        ...prev,
        currentX: x,
        currentY: y,
      }));
      return;
    }

    // Dragging
    if (dragState.isDragging && dragState.mappingId) {
      const currentMapping = visibleMappings.find(m => m.id === dragState.mappingId);
      if (currentMapping) {
        const rawX = Math.max(0, x - dragState.offsetX);
        const rawY = Math.max(0, y - dragState.offsetY);

        // Find snap positions
        const { snappedX, snappedY, snapLineX, snapLineY } = findSnapPositions(
          dragState.mappingId,
          rawX,
          rawY,
          currentMapping.width,
          currentMapping.height
        );

        setSnapLines({ x: snapLineX, y: snapLineY });

        onUpdateMapping(dragState.mappingId, {
          x: snappedX,
          y: snappedY,
        });
      }
      return;
    }

    // Resizing
    if (resizeState.isResizing && resizeState.mappingId && resizeState.originalMapping) {
      const orig = resizeState.originalMapping;
      const dx = x - resizeState.startX;
      const dy = y - resizeState.startY;

      let newX = orig.x;
      let newY = orig.y;
      let newWidth = orig.width;
      let newHeight = orig.height;

      switch (resizeState.handle) {
        case 'se':
          newWidth = Math.max(20, orig.width + dx);
          newHeight = Math.max(10, orig.height + dy);
          break;
        case 'sw':
          newX = Math.min(orig.x + orig.width - 20, orig.x + dx);
          newWidth = Math.max(20, orig.width - dx);
          newHeight = Math.max(10, orig.height + dy);
          break;
        case 'ne':
          newY = Math.min(orig.y + orig.height - 10, orig.y + dy);
          newWidth = Math.max(20, orig.width + dx);
          newHeight = Math.max(10, orig.height - dy);
          break;
        case 'nw':
          newX = Math.min(orig.x + orig.width - 20, orig.x + dx);
          newY = Math.min(orig.y + orig.height - 10, orig.y + dy);
          newWidth = Math.max(20, orig.width - dx);
          newHeight = Math.max(10, orig.height - dy);
          break;
        case 'e':
          newWidth = Math.max(20, orig.width + dx);
          break;
        case 'w':
          newX = Math.min(orig.x + orig.width - 20, orig.x + dx);
          newWidth = Math.max(20, orig.width - dx);
          break;
        case 'n':
          newY = Math.min(orig.y + orig.height - 10, orig.y + dy);
          newHeight = Math.max(10, orig.height - dy);
          break;
        case 's':
          newHeight = Math.max(10, orig.height + dy);
          break;
      }

      onUpdateMapping(resizeState.mappingId, {
        x: newX,
        y: newY,
        width: newWidth,
        height: newHeight,
      });
    }
  }, [drawState.isDrawing, dragState, resizeState, getMousePosition, onUpdateMapping]);

  // Handle mouse up
  const handleMouseUp = useCallback(async () => {
    // Complete drawing
    if (drawState.isDrawing && drawingField) {
      const minX = Math.min(drawState.startX, drawState.currentX);
      const minY = Math.min(drawState.startY, drawState.currentY);
      const regionWidth = Math.abs(drawState.currentX - drawState.startX);
      const regionHeight = Math.abs(drawState.currentY - drawState.startY);

      // Only create if region is large enough
      if (regionWidth > 10 && regionHeight > 5) {
        const fieldDef = AVAILABLE_FIELDS.find(f => f.name === drawingField);
        const fieldType = fieldDef?.type || 'text';

        // Try to detect font from PDF
        let fontFamily = 'Helvetica';
        let fontSize = 10;
        let fontColor = '#000000';

        if (getFontInfo) {
          try {
            const fontInfo = await getFontInfo(minX, minY, regionWidth, regionHeight, pageNumber);
            if (fontInfo) {
              fontFamily = fontInfo.fontFamily;
              fontSize = fontInfo.fontSize;
              fontColor = fontInfo.fontColor;
              console.log('Detected font:', fontInfo);
            }
          } catch (err) {
            console.error('Error detecting font:', err);
          }
        }

        onCreateMapping({
          fieldName: drawingField,
          fieldType,
          pageNumber,
          x: minX,
          y: minY,
          width: regionWidth,
          height: regionHeight,
          fontFamily,
          fontSize,
          fontColor,
          textAlign: fieldType === 'currency' || fieldType === 'number' ? 'right' : 'left',
          lineHeight: 1.2,
        });
      }
    }

    setDrawState({
      isDrawing: false,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
    });
    setDragState({
      isDragging: false,
      mappingId: null,
      startX: 0,
      startY: 0,
      offsetX: 0,
      offsetY: 0,
    });
    setResizeState({
      isResizing: false,
      mappingId: null,
      handle: null,
      startX: 0,
      startY: 0,
      originalMapping: null,
    });
    setSnapLines({ x: null, y: null });
  }, [drawState, drawingField, pageNumber, onCreateMapping, getFontInfo]);

  // Handle key press for delete
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedMapping) {
        e.preventDefault();
        onDeleteMapping(selectedMapping);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedMapping, onDeleteMapping]);

  // Render a single mapping region
  const renderMapping = (mapping: MappingRegion) => {
    const isSelected = mapping.id === selectedMapping;
    const isHovered = mapping.id === hoveredMapping;
    const color = FIELD_COLORS[mapping.fieldType];
    const fieldDef = AVAILABLE_FIELDS.find(f => f.name === mapping.fieldName);
    const shortLabel = SHORT_LABELS[mapping.fieldName] || mapping.fieldName.substring(0, 6);
    const fullLabel = fieldDef?.label || mapping.fieldName;

    return (
      <div
        key={mapping.id}
        className={`absolute border-2 ${isSelected ? 'border-solid' : 'border-dashed'} cursor-move`}
        style={{
          left: mapping.x * scale,
          top: mapping.y * scale,
          width: mapping.width * scale,
          height: mapping.height * scale,
          borderColor: color,
          backgroundColor: `${color}20`,
        }}
        onMouseEnter={() => setHoveredMapping(mapping.id)}
        onMouseLeave={() => setHoveredMapping(null)}
      >
        {/* Short label inside box at bottom-right - always visible */}
        <div
          className="absolute bottom-0 right-0 text-[9px] px-0.5 rounded-tl opacity-70"
          style={{
            backgroundColor: color,
            color: 'white',
            fontSize: '9px',
            lineHeight: '1.1',
          }}
        >
          {shortLabel}
        </div>

        {/* Full label tooltip - shown on hover or select, positioned ABOVE the box */}
        {(isHovered || isSelected) && (
          <div
            className="absolute left-0 text-xs px-1 py-0.5 rounded whitespace-nowrap z-50 shadow-md"
            style={{
              backgroundColor: color,
              color: 'white',
              bottom: '100%',
              marginBottom: '4px',
            }}
          >
            {fullLabel}
          </div>
        )}

        {/* Resize handles (only for selected) */}
        {isSelected && (
          <>
            <div className="absolute -right-1 -bottom-1 w-3 h-3 bg-white border-2 cursor-se-resize" style={{ borderColor: color }} />
            <div className="absolute -left-1 -bottom-1 w-3 h-3 bg-white border-2 cursor-sw-resize" style={{ borderColor: color }} />
            <div className="absolute -right-1 -top-1 w-3 h-3 bg-white border-2 cursor-ne-resize" style={{ borderColor: color }} />
            <div className="absolute -left-1 -top-1 w-3 h-3 bg-white border-2 cursor-nw-resize" style={{ borderColor: color }} />
          </>
        )}
      </div>
    );
  };

  // Render drawing preview
  const renderDrawingPreview = () => {
    if (!drawState.isDrawing || !drawingField) return null;

    const fieldDef = AVAILABLE_FIELDS.find(f => f.name === drawingField);
    const color = FIELD_COLORS[fieldDef?.type || 'text'];
    const shortLabel = SHORT_LABELS[drawingField] || drawingField.substring(0, 6);
    const fullLabel = fieldDef?.label || drawingField;

    const minX = Math.min(drawState.startX, drawState.currentX);
    const minY = Math.min(drawState.startY, drawState.currentY);
    const width = Math.abs(drawState.currentX - drawState.startX);
    const height = Math.abs(drawState.currentY - drawState.startY);

    return (
      <div
        className="absolute border-2 border-dashed pointer-events-none"
        style={{
          left: minX * scale,
          top: minY * scale,
          width: width * scale,
          height: height * scale,
          borderColor: color,
          backgroundColor: `${color}30`,
        }}
      >
        {/* Short label inside box */}
        <div
          className="absolute bottom-0 right-0 text-[9px] px-0.5 rounded-tl opacity-70"
          style={{
            backgroundColor: color,
            color: 'white',
            fontSize: '9px',
            lineHeight: '1.1',
          }}
        >
          {shortLabel}
        </div>
        {/* Full label above box while drawing */}
        <div
          className="absolute left-0 text-xs px-1 py-0.5 rounded whitespace-nowrap z-50 shadow-md"
          style={{
            backgroundColor: color,
            color: 'white',
            bottom: '100%',
            marginBottom: '4px',
          }}
        >
          {fullLabel}
        </div>
      </div>
    );
  };

  // Render snap guide lines
  const renderSnapLines = () => {
    return (
      <>
        {snapLines.x !== null && (
          <div
            className="absolute pointer-events-none"
            style={{
              left: snapLines.x * scale,
              top: 0,
              width: 1,
              height: height * scale,
              backgroundColor: '#EF4444',
              opacity: 0.8,
            }}
          />
        )}
        {snapLines.y !== null && (
          <div
            className="absolute pointer-events-none"
            style={{
              left: 0,
              top: snapLines.y * scale,
              width: width * scale,
              height: 1,
              backgroundColor: '#EF4444',
              opacity: 0.8,
            }}
          />
        )}
      </>
    );
  };

  return (
    <div
      ref={canvasRef}
      className={`absolute top-0 left-0 ${drawingField ? 'cursor-crosshair' : ''}`}
      style={{ width: width * scale, height: height * scale }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {visibleMappings.map(renderMapping)}
      {renderDrawingPreview()}
      {renderSnapLines()}
    </div>
  );
};

export default MappingCanvas;
